"use client"

import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import { t } from "@/languages/i18n"
import { PostData } from "@/lib/config/types"
import { DefaultImage } from "@/lib/external-components"
import Image from "next/image"
import Link from "next/link"
import { useState } from "react"

interface PostCardItemProps {
  post: PostData
  isDragging?: boolean
}

export const PostCardItem: React.FC<PostCardItemProps> = ({ post, isDragging }) => {
  const [hasMoved, setHasMoved] = useState(false)
  const [startPos, setStartPos] = useState({ x: 0, y: 0 })

  const handleMouseDown = (e: React.MouseEvent) => {
    setStartPos({ x: e.clientX, y: e.clientY })
    setHasMoved(false)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    const deltaX = Math.abs(e.clientX - startPos.x)
    const deltaY = Math.abs(e.clientY - startPos.y)

    if (deltaX > 5 || deltaY > 5) {
      setHasMoved(true)
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    if (hasMoved || isDragging) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const content = (
    <>
      <div className="relative h-[223.17px] w-full overflow-hidden rounded-3xl">
        <Image
          src={
            post.attributes.image?.data?.attributes?.url
              ? `${ADMIN_API_CONFIG.API_URL}${post.attributes.image?.data?.attributes?.url}`
              : DefaultImage
          }
          alt={post.attributes.name}
          fill
          className="object-cover rounded-3xl w-full h-full group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-col">
          {post.attributes.readingTime && (
            <p className="text-sm text-black group-hover:text-gray-600 flex items-center transition-colors duration-300">
              <span className="h-[5px] w-[5px] bg-[#07121F] group-hover:bg-gray-600 rounded-full inline-block mr-2 transition-colors duration-300"></span>{" "}
              {post.attributes.readingTime} {t("Minutes Reading")}
            </p>
          )}
          <p className="text-base leading-5.5 font-semibold text-[#07121F] group-hover:text-gray-600 transition-colors duration-300">
            {post.attributes.name}
          </p>
        </div>
        {post.attributes.shortDescription && (
          <p className="text-base leading-5.5 font-normal text-[#07121F] group-hover:text-gray-600 line-clamp-3 transition-colors duration-300">
            {post.attributes.shortDescription}
          </p>
        )}
      </div>
    </>
  )

  return (
    <div
      key={post.id}
      className="flex-shrink-0 w-full md:w-[373.33px]"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
    >
      <Link
        href={`/blog/${post.attributes.slug}`}
        className="flex flex-col gap-4 group hover:opacity-90 transition-all duration-300"
        onClick={handleClick}
      >
        {content}
      </Link>
    </div>
  )
}

export default PostCardItem
